import { GovField } from "@publicMirage/components/govReact/GovField";
import { H3, InsetText, ListItem, MultiChoice, UnorderedList } from "govuk-react";
import React, { memo, useCallback, useEffect } from "react";
import { useForm } from "../hooks/formContext";
import { FileContent } from "@shared/mirage/core/contracts/UploadedFile";
import DragAndDropFiles from "@mirage/components/dragAndDrop/DragAndDropFiles";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";

export const PrePpWlOptionalSection = memo(() => {
    const { toFieldProps, setFieldValue, formState, setFormState } = useForm();
    const { optionalDocuments, blockPlanProvided, designStatementProvided, existingDetailsProvided, photographsProvided, proposedDetailsProvided } = formState;
    const anyOptionalDocumentsProvided =
        blockPlanProvided.value || designStatementProvided.value || existingDetailsProvided.value || photographsProvided.value || proposedDetailsProvided.value;
    const uploadOnChange = useCallback(
        async (_: FileContent[], originalFiles: File[] | null) => {
            const docs = originalFiles?.map((file) => {
                return new File([file], file.name, { type: file.type });
            });
            setFieldValue("optionalDocuments", docs);
        },
        [setFieldValue]
    );
    const removeAttachment = (index: number) => {
        setFormState((prev) => {
            return {
                ...prev,
                optionalDocuments: {
                    errorMsg: prev.optionalDocuments.errorMsg,
                    value: prev.optionalDocuments.value?.filter((_, i) => i !== index),
                },
            };
        });
    };

    useEffect(() => {
        if (!anyOptionalDocumentsProvided) {
            setFormState((prev) => {
                return {
                    ...prev,
                    optionalDocuments: {
                        ...prev.optionalDocuments,
                        value: [],
                    },
                };
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anyOptionalDocumentsProvided]);

    const showDocumentFields =
        blockPlanProvided.value || existingDetailsProvided.value || proposedDetailsProvided.value || photographsProvided.value || designStatementProvided.value;
    return (
        <>
            <H3>Optional</H3>
            <InsetText>
                The following is required in addition to completing this form. Please note, your application cannot move forward without these.
                <UnorderedList>
                    <ListItem>
                        Block Plan - to a scale of 1:500 or 1:200, the layout should identify the site boundaries, site dimensions, trees within the site and surrounding land, the
                        position of roads, footpaths, and rights of way plus any other features that should be taken in to account.
                    </ListItem>
                    <ListItem>Existing Details - showing the current building in the form of elevation drawings and floor plans to a scale of 1:50 or 1:100.</ListItem>
                    <ListItem>Proposed Details - showing what is proposed in the form of elevation drawings and floor plans to a scale of 1:50 or 1:100.</ListItem>
                    <ListItem>Photographs - showing the site and immediate surroundings.</ListItem>
                    <ListItem>Design Statement - including the materials that might be used.</ListItem>
                </UnorderedList>
            </InsetText>
            <MultiChoice label="If you have these already, select all that apply">
                <GovField {...toFieldProps("blockPlanProvided", "CHECKBOX")} />
                <GovField {...toFieldProps("existingDetailsProvided", "CHECKBOX")} />
                <GovField {...toFieldProps("proposedDetailsProvided", "CHECKBOX")} />
                <GovField {...toFieldProps("photographsProvided", "CHECKBOX")} />
                <GovField {...toFieldProps("designStatementProvided", "CHECKBOX")} />
            </MultiChoice>
            {showDocumentFields ? (
                <>
                    <DragAndDropFiles onDrop={uploadOnChange} onClick={uploadOnChange} label="Drop file or click to upload optional documents." />
                    <Box sx={{ display: "flex", "flex-wrap": "wrap", gap: "0.5rem", margin: "1rem 0" }}>
                        {optionalDocuments.value?.map((attachment, index) => {
                            return (
                                <Chip
                                    label={attachment.name}
                                    onDelete={() => removeAttachment(index)}
                                    key={`${attachment.name}-${index}`}
                                    sx={{ backgroundColor: "rgba(3, 87, 166, 0.2)" }}
                                />
                            );
                        })}
                    </Box>
                    <GovField {...toFieldProps("hardCopyOptionalDocumentsWillBeProvided", "CHECKBOX")} />
                </>
            ) : null}

            <GovField {...toFieldProps("otherInformation", "TEXTAREA")} />
        </>
    );
});
